import { NgIcon, provideIcons } from '@ng-icons/core';
import { matArrowBack } from '@ng-icons/material-icons/baseline';
import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ForgotPasswordComponent } from '@components/auth/login/forgot-password/forgot-password.component';
import { FormRegisterComponent } from '@components/auth/login/form-register/form-register.component';
import { AuthenService } from '@core/authen/authen.service';
import { CentralConfigService } from '@core/services/central-config.service';
import { UserConfigManagementService } from '@core/services/user-config-management.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { InfoNoticeComponent } from '@shared/components/atoms/info-notice/info-notice.component';
import { BaseComponentWithServiceComponent } from '@shared/components/base-list/base-component-with-service/base-component-with-service.component';
import { FormLoginComponent } from '@shared/components/form-login/form-login.component';
import { JipLogoComponent } from '@shared/components/jip-logo/jip-logo.component';
import { LoginWithLinkedinBtnComponent } from '@shared/components/login-with-linkedin-btn/login-with-linkedin-btn.component';
import { OrganizationLogoComponent } from '@shared/components/molecules/organization-logo/organization-logo.component';
import { ToBoolean } from '@shared/decorators/to-boolean';
import { ToNumber } from '@shared/decorators/to-number';
import { LanguagePathDirective } from '@shared/directives/language-path.directive';
import { CredentialDisplayMode } from '@shared/enums/credential-display-mode.enum';
import { StorageEnum } from '@shared/enums/storage.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { TwoFactorVerificationPayload } from '@shared/interfaces/request/authen.interface';
import { TenantInterface } from '@shared/interfaces/tenant.interface';
import { LogoDisplayOption } from '@shared/models/logo-type.model';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { FormTwoFactorComponent } from './form-two-factor/form-two-factor.component';

@UntilDestroy()
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  imports: [
    NgIcon,
    CommonModule,
    JipLogoComponent,
    OrganizationLogoComponent,
    TranslateModule,
    InfoNoticeComponent,
    FormLoginComponent,
    LanguagePathDirective,
    FormRegisterComponent,
    LoginWithLinkedinBtnComponent,
    ForgotPasswordComponent,
    FormTwoFactorComponent,
  ],
  viewProviders: [provideIcons({ matArrowBack })],
})
export class LoginComponent
  extends BaseComponentWithServiceComponent
  implements OnInit, OnChanges
{
  @Input() email: string;

  @Input() globalOrgId: string; // Central config

  @Input() eventId: string;

  @Input() logoDisplayOption: LogoDisplayOption = 'jip';

  @Input() termOfUseUrl = environment.termOfUseUrl;

  @Input() portalName = environment.portalName;

  @Input() portalUrl = environment.portalUrl;

  @Input() @ToBoolean() auto_assign_to_org_enabled = true;

  @Input() @ToNumber() assignToOrgId: number;

  // @HostListener('document:visibilitychange', ['$event'])
  // handleVisibilityChange(): void {
  //   this.handleLoginInit();
  // }

  credentialDisplayMode: CredentialDisplayMode = CredentialDisplayMode.Login;
  isShowBackLogin = true;
  isSentConfirmationEmail = false;
  isPasswordResetEmailSent = false;
  isAccountNotActive = false;
  isTwoFactorVerified = false;

  invitationTokens = '';
  sessionExpired = false;
  actionParamKey = UrlParam.Action;
  // Two factor
  credentialOTP: TwoFactorVerificationPayload;

  globalOrganizationId: number;

  readonly userConfig = inject(UserConfigManagementService);

  constructor(
    readonly authenService: AuthenService,
    readonly sessionService: SessionService,
    readonly centralConfig: CentralConfigService
  ) {
    super(sessionService);
  }

  ngOnInit(): void {
    this.handleLoginInit();

    this.sessionService.tenant$
      .pipe(untilDestroyed(this))
      .subscribe((tenant: TenantInterface) => {
        if (tenant?.termsOfServiceUrl)
          this.termOfUseUrl = tenant.termsOfServiceUrl;
      });

    this.handleGlobalOrganization().then();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.termOfUseUrl) {
      if (changes.termOfUseUrl.currentValue && !this.termOfUseUrl) {
        this.termOfUseUrl = changes.termOfUseUrl.currentValue;
      }
    }
  }

  handleLoginInit() {
    const token = StringUtils.getParamFromUrl(UrlParam.Token);

    if (token) {
      this.invitationTokens = token;
      this.toggleRegistrationForm();
    } else if (this.sessionService.authenticated) {
      this.goBack();
    }

    this.sessionExpired = Boolean(
      this.sessionService.getCookie(StorageEnum.isTokenExpired)
    );

    this.checkAndUpdateCredentialDisplayMode();
  }

  async handleGlobalOrganization(): Promise<void> {
    const orgIds = await this.userConfig.get(StorageEnum.currentFilterToken);

    this.globalOrganizationId =
      this.centralConfig.innovationSpaceId ?? orgIds[0];
  }

  toggleLoginForm(event?: Event): void {
    event?.preventDefault();
    this.isAccountNotActive = false;
    this.isSentConfirmationEmail = false;

    this.updateActionParamOnUrl(CredentialDisplayMode.Login);
  }

  toggleRegistrationForm(event?: Event): void {
    event?.preventDefault();
    this.isAccountNotActive = false;
    this.isSentConfirmationEmail = false;

    this.updateActionParamOnUrl(CredentialDisplayMode.Register);
  }

  toggleForgotPasswordForm(event?: Event): void {
    event?.preventDefault();
    this.updateActionParamOnUrl(CredentialDisplayMode.ForgotPassword);
  }

  toggleTwoFactorForm(data?: TwoFactorVerificationPayload): void {
    this.credentialOTP = data;
    this.updateActionParamOnUrl(CredentialDisplayMode.TwoFactor);
  }

  onDisplayedAccountNotActive(): void {
    this.isAccountNotActive = true;
  }

  onSentResetPasswordEmail(): void {
    this.isPasswordResetEmailSent = true;
  }

  onVerifyTwoFactor(): void {
    this.isTwoFactorVerified = true;
  }

  setEventDetailPageCallback(): void {
    if (!this.eventId) return;

    const eventDetail = this.sessionService.appendLanguagePath(
      `${environment.jipUrl.event}/${this.eventId}?${UrlParam.RegisterEvent}=true`
    );

    this.sessionService.setLoginCallBackpage(eventDetail);
  }

  goBack(): void {
    const url = this.sessionService.getLoginCallBackpage() || location.origin;
    if (url.includes(environment.jipUrl.login)) {
      FormUtils.navigateTo(this.sessionService.appendLanguagePath('/'));
    } else {
      FormUtils.navigateTo(url);
    }
  }

  onConfirmationEmailSent(): void {
    this.isSentConfirmationEmail = true;
  }

  private checkAndUpdateCredentialDisplayMode(): void {
    const action = StringUtils.getParamFromUrl(this.actionParamKey);

    if (!action) {
      this.credentialDisplayMode = CredentialDisplayMode.Login;
      return;
    }

    const credentialKey = Object.keys(CredentialDisplayMode).find(
      k => CredentialDisplayMode[k] === action
    );

    if (credentialKey) {
      this.credentialDisplayMode = CredentialDisplayMode[credentialKey];
    }
  }

  private updateActionParamOnUrl(credentialMode: string): void {
    if (credentialMode === CredentialDisplayMode.Login) {
      StringUtils.removeParamFromUrl(this.actionParamKey);
    } else {
      StringUtils.setQueryParamOnUrl(this.actionParamKey, credentialMode);
    }
    this.checkAndUpdateCredentialDisplayMode();
  }
}
